import { Clipboard } from '@angular/cdk/clipboard';
import { Injector } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  BaseWidgetProperties,
  ButtonActionType,
  UiZoneWidgetAttributeConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { WorkflowFormsService } from '../../../services/workflow-forms/workflow-forms.service';
import { AccountState } from '../../../state/account.state';
import { createScriptedActionState, executeScriptedAction } from './scripted-action.runtime';

export function createNestedActionAttributes(
  attributes: UiZoneWidgetAttributeConfig,
  actionType: ButtonActionType,
  navigation?: Record<string, unknown>,
): UiZoneWidgetAttributeConfig {
  const widgetProperties: BaseWidgetProperties = {
    ...attributes.widgetProperties,
    buttonActionType: actionType,
  };
  delete widgetProperties['actionNavigationConfigurations'];
  delete widgetProperties['actionButtonNavigationConfig'];
  if (navigation) {
    widgetProperties['actionNavigationConfigurations'] = navigation;
    widgetProperties['actionButtonNavigationConfig'] = navigation;
  }
  return { ...attributes, widgetProperties };
}

export async function runScriptedAction(
  injector: Injector,
  attributes: UiZoneWidgetAttributeConfig,
): Promise<void> {
  const script = attributes.widgetProperties?.['actionScript'] ?? '';
  const store = injector.get(Store);
  const route = injector.get(ActivatedRoute);
  const router = injector.get(Router);
  const alertHandler = injector.get(AlertHandlerService);
  const workflowForms = injector.get(WorkflowFormsService);

  try {
    const state = createScriptedActionState(store.snapshot());
    const commands = await executeScriptedAction(script, {
      state,
      route: {
        params: route.snapshot.params,
        queryParams: route.snapshot.queryParams,
        url: router.url,
      },
      widgetContext: attributes.widgetProperties?.widgetContext,
    });

    for (const command of commands) {
      if (command.type === 'navigate') {
        await router.navigateByUrl(command.url);
      }
      if (command.type === 'notify') {
        alertHandler.showAlertFn(command.level, command.body, command.title);
      }
      if (command.type === 'setFormValue') {
        const control = workflowForms.getFormPart(command.formPath)?.get(command.fieldPath);
        if (!control) {
          throw new Error(
            `Scripted action could not find field "${command.fieldPath}" in form "${command.formPath}".`,
          );
        }
        control.setValue(command.value);
      }
      if (command.type === 'copyToClipboard') {
        const copied = injector.get(Clipboard).copy(command.value);
        if (!copied) {
          throw new Error('Scripted action could not copy the requested text to the clipboard.');
        }
      }
      if (command.type === 'runAction') {
        if (command.actionType === ButtonActionType.SCRIPTED_ACTION) {
          throw new Error('A scripted action cannot invoke another scripted action.');
        }

        const { ctaButtonFunctionsMap } = await import('../cta-button-actions.utils');
        const nestedAction = ctaButtonFunctionsMap.get(command.actionType as ButtonActionType);
        if (!nestedAction) {
          throw new Error(`Scripted action requested an unknown action: "${command.actionType}".`);
        }

        const nestedAttributes = createNestedActionAttributes(
          attributes,
          command.actionType as ButtonActionType,
          command.navigation,
        );
        await nestedAction(injector, nestedAttributes);
      }
    }
  } catch (error) {
    alertHandler.showAlertFn('error', 'This action could not be completed.');
    throw error;
  }
}
